'use client';

import { c as _c } from "react/compiler-runtime";
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { keyBy, map, merge, omit } from 'es-toolkit/compat';
import { atom } from 'jotai';
import { listSubscriptionTiers } from '@/api/virtual-lab-svc/queries/subscription';
import { getSanityTiers } from '@/services/sanity';
import { classNames } from '@/util/utils';
import type { ComponentPropsWithRef } from 'react';
import type { ContentForPricing } from '@/components/LandingPage/content/pricing';
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = {
  [key: string]: JsonValue;
};
type JsonArray = Array<JsonValue>;
type PriceValue = {
  value: number;
  currency: string;
};
type TierPrice = {
  month: Array<PriceValue>;
  discount: Array<PriceValue>;
  yearNormal: Array<PriceValue>;
  yearDiscount: Array<PriceValue>;
};
export type TTierFeature = {
  title: string;
  specialLabel?: Array<string>;
  tooltip?: Array<string>;
};
type FeatureCategory = {
  title: string;
  available: boolean;
  featuresList: Array<TTierFeature>;
};
export type TSingleTier = {
  id: string;
  title: string;
  notes?: Array<string>;
  price?: TierPrice;
  features: Array<FeatureCategory>;
};
export type Interval = 'month' | 'year';
export type TExtendedTier = TSingleTier & {
  app_id: string;
  sanity_id: string;
  prices: Array<{
    id: string;
    amount: number;
    currency: string;
    interval: Interval;
    nickname: string;
    discount: number;
  }>;
  metadata: Record<string, string>;
};
type TiersData = {
  tiers: TSingleTier[];
};
export const FlowStepDict = {
  Select: 'select',
  EmailVerification: 'email-verification',
  Pay: 'pay'
} as const;
type TFlowStep = (typeof FlowStepDict)[keyof typeof FlowStepDict];
export const IntervalDict = {
  Month: 'month',
  Year: 'year'
} as const;
type TInterval = (typeof IntervalDict)[keyof typeof IntervalDict];
export const DefaultCurrency = 'chf';
export const flowAtom = atom<{
  step: TFlowStep | null;
  tier: TExtendedTier | null;
  interval: TInterval;
  currency?: string;
}>({
  step: FlowStepDict.Select,
  tier: null,
  interval: IntervalDict.Month,
  currency: DefaultCurrency
});
export function Switch(t0) {
  const $ = _c(16);
  let className;
  let props;
  let ref;
  let thumbCls;
  if ($[0] !== t0) {
    ({
      className,
      thumbCls,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = thumbCls;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    thumbCls = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = classNames("peer inline-flex h-5 w-11 shrink-0 cursor-pointer items-center", "data-[state=unchecked]:bg-input border-primary-8 rounded-full border-2 transition-colors", "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden", "focus-visible:ring-offset-background data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-50", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== thumbCls) {
    t2 = classNames("bg-background pointer-events-none block h-3 w-3 rounded-full shadow-lg ring-0 transition-transform", "data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0", thumbCls);
    $[7] = thumbCls;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <SwitchPrimitives.Thumb className={t2} />;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== ref || $[13] !== t1 || $[14] !== t3) {
    t4 = <SwitchPrimitives.Root className={t1} {...props} ref={ref}>{t3}</SwitchPrimitives.Root>;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function transformData(data: ContentForPricing): TiersData {
  const transformedTiers: TSingleTier[] = data.plans.map(plan => {
    const tierFeatures: FeatureCategory[] = data.features.map(category => {
      // transform features within this category for this plan
      const featuresList = category.features?.flatMap(feature => {
        // Check if this feature is available for the current plan
        const planFeature = feature.plans?.find(p => p.id === plan.id);
        if (!planFeature) {
          return [];
        }
        const transformedFeature: TTierFeature = {
          title: feature.title
        };
        if (planFeature.label) {
          transformedFeature.specialLabel = [planFeature.label];
        }
        if (planFeature.tooltip) {
          transformedFeature.tooltip = [planFeature.tooltip];
        }
        return [transformedFeature];
      });
      return {
        title: category.title,
        available: category.available,
        featuresList
      };
    });
    const transformedTier: TSingleTier = {
      id: plan.id,
      title: plan.title,
      features: tierFeatures
    };
    if (plan.notes && plan.notes.length > 0) {
      transformedTier.notes = plan.notes;
    }
    if (plan.price) {
      transformedTier.price = {
        month: plan.price.month || [],
        discount: plan.price.discount ?? [],
        yearNormal: plan.price.yearNormal || [],
        yearDiscount: plan.price.yearDiscount || []
      };
    }
    return transformedTier;
  });
  return {
    tiers: transformedTiers
  };
}
const renameAndRemove = (arr: Array<Record<string, unknown>>, oldKey: string, newKey: string) => map(arr, obj => obj[oldKey] !== undefined ? {
  ...omit(obj, oldKey),
  [newKey]: obj[oldKey]
} : obj);
export async function getAllTiers(): Promise<Array<TExtendedTier>> {
  const [appTiers, sanityTiers] = await Promise.all([listSubscriptionTiers(), getSanityTiers()]);
  if (!appTiers || !sanityTiers) {
    throw new Error('Tiers can not be fetched');
  }
  const tiers1 = keyBy(renameAndRemove(appTiers.tiers, 'id', 'app_id'), 'sanity_id');
  const tiers2 = keyBy(transformData(sanityTiers).tiers, 'id');
  const tiers = Object.values(merge({}, tiers1, tiers2)) as Array<TExtendedTier>;
  return tiers;
}